import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Edit, Trash2, Save, RefreshCw, Upload, Star, Youtube, Users } from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';

// Helper function to get full image URL - fixes URLs from old domains
const getFullImageUrl = (url) => {
  if (!url) return '';
  if (url.includes('/api/uploads/')) {
    const match = url.match(/\/api\/uploads\/[^\s"']+/);
    if (match) return `${window.location.origin}${match[0]}`;
  }
  if (url.startsWith('http')) return url;
  return `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`;
};

const CMSTestimonialsPage = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState({ open: false, data: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try { const res = await api.get('/cms/testimonials'); setTestimonials(res.data); }
    catch (error) { toast.error('Erreur'); } finally { setLoading(false); }
  };

  const handleSave = async () => {
    const { data } = editModal;
    try {
      if (data.id) { await api.put(`/cms/testimonials/${data.id}`, data); }
      else { await api.post('/cms/testimonials', data); }
      toast.success('Enregistré'); loadData(); setEditModal({ open: false, data: null });
    } catch (error) { toast.error('Erreur'); }
  };

  const handleDelete = async () => {
    try { await api.delete(`/cms/testimonials/${deleteModal.id}`); toast.success('Supprimé'); loadData(); setDeleteModal({ open: false, id: null }); }
    catch (error) { toast.error('Erreur'); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const formData = new FormData(); formData.append('file', file); formData.append('folder', 'testimonials');
    try { const res = await api.post('/cms/upload', formData); setEditModal({...editModal, data: {...editModal.data, avatar: res.data.url}}); toast.success('Photo uploadée'); }
    catch (error) { toast.error('Erreur upload'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="h-8 w-8 animate-spin text-cyan-500" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold text-slate-900">Témoignages</h1><p className="text-slate-500">Avis clients écrits ou vidéo</p></div>
        <Button onClick={() => setEditModal({ open: true, data: { is_active: true, rating: 5, is_video: false, order: testimonials.length + 1 } })} className="bg-gradient-to-r from-cyan-500 to-blue-600"><Plus className="w-4 h-4 mr-2" />Nouveau témoignage</Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {testimonials.map(testimonial => (
          <Card key={testimonial.id}>
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden">
                  {testimonial.avatar ? <img src={getFullImageUrl(testimonial.avatar)} alt={testimonial.name} className="w-full h-full object-cover" /> : <Users className="h-6 w-6 text-slate-400" />}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold">{testimonial.name}</h3>
                    {testimonial.is_video && <Badge variant="secondary" className="text-xs"><Youtube className="h-3 w-3 mr-1" />Vidéo</Badge>}
                  </div>
                  <p className="text-sm text-slate-500">{testimonial.role} - {testimonial.company}</p>
                  <div className="flex gap-0.5 mt-1">{[...Array(testimonial.rating || 5)].map((_, i) => <Star key={i} className="h-3 w-3 fill-amber-400 text-amber-400" />)}</div>
                </div>
              </div>
              <p className="mt-4 text-sm text-slate-600 italic line-clamp-3">"{testimonial.quote}"</p>
              <div className="mt-4 flex justify-between items-center">
                <Badge variant="outline">{testimonial.service}</Badge>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => setEditModal({ open: true, data: testimonial })}><Edit className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => setDeleteModal({ open: true, id: testimonial.id })}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={editModal.open} onOpenChange={(open) => !open && setEditModal({ open: false, data: null })}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editModal.data?.id ? 'Modifier' : 'Nouveau'} témoignage</DialogTitle></DialogHeader>
          {editModal.data && (
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2"><Label>Nom</Label><Input value={editModal.data.name || ''} onChange={(e) => setEditModal({...editModal, data: {...editModal.data, name: e.target.value}})} /></div>
                <div className="space-y-2"><Label>Poste</Label><Input value={editModal.data.role || ''} onChange={(e) => setEditModal({...editModal, data: {...editModal.data, role: e.target.value}})} /></div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2"><Label>Entreprise</Label><Input value={editModal.data.company || ''} onChange={(e) => setEditModal({...editModal, data: {...editModal.data, company: e.target.value}})} /></div>
                <div className="space-y-2"><Label>Service</Label><Select value={editModal.data.service || ''} onValueChange={(v) => setEditModal({...editModal, data: {...editModal.data, service: v}})}><SelectTrigger><SelectValue placeholder="Choisir..." /></SelectTrigger><SelectContent><SelectItem value="web">Web</SelectItem><SelectItem value="graphique">Graphique</SelectItem><SelectItem value="video">Vidéo</SelectItem><SelectItem value="marketing">Marketing</SelectItem></SelectContent></Select></div>
              </div>
              <div className="space-y-2"><Label>Témoignage</Label><Textarea value={editModal.data.quote || ''} onChange={(e) => setEditModal({...editModal, data: {...editModal.data, quote: e.target.value}})} rows={3} /></div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2"><Label>Note (1-5)</Label><Select value={String(editModal.data.rating || 5)} onValueChange={(v) => setEditModal({...editModal, data: {...editModal.data, rating: parseInt(v)}})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{[1,2,3,4,5].map(n => <SelectItem key={n} value={String(n)}>{n} étoile{n > 1 ? 's' : ''}</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-2"><Label>Type de projet</Label><Input value={editModal.data.project_type || ''} onChange={(e) => setEditModal({...editModal, data: {...editModal.data, project_type: e.target.value}})} placeholder="Site vitrine + SEO" /></div>
              </div>
              <div className="space-y-2"><Label>Photo/Avatar</Label><div className="flex gap-2"><Input value={editModal.data.avatar || ''} onChange={(e) => setEditModal({...editModal, data: {...editModal.data, avatar: e.target.value}})} className="flex-1" /><label className="cursor-pointer"><Button variant="outline" as="span"><Upload className="h-4 w-4" /></Button><input type="file" className="hidden" accept="image/*" onChange={handleUpload} /></label></div></div>
              <div className="flex items-center gap-2"><Switch checked={editModal.data.is_video || false} onCheckedChange={(checked) => setEditModal({...editModal, data: {...editModal.data, is_video: checked}})} /><Label>Témoignage vidéo</Label></div>
              {editModal.data.is_video && <div className="space-y-2"><Label>URL YouTube</Label><Input value={editModal.data.youtube_url || ''} onChange={(e) => setEditModal({...editModal, data: {...editModal.data, youtube_url: e.target.value}})} placeholder="https://youtube.com/watch?v=..." /></div>}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setEditModal({ open: false, data: null })}>Annuler</Button><Button onClick={handleSave} className="bg-gradient-to-r from-cyan-500 to-blue-600"><Save className="w-4 h-4 mr-2" />Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteModal.open} onOpenChange={(open) => !open && setDeleteModal({ open: false, id: null })}>
        <DialogContent><DialogHeader><DialogTitle>Supprimer ce témoignage ?</DialogTitle></DialogHeader>
          <DialogFooter><Button variant="outline" onClick={() => setDeleteModal({ open: false, id: null })}>Annuler</Button><Button variant="destructive" onClick={handleDelete}>Supprimer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CMSTestimonialsPage;
